/**
 * Phase B.1 — `BurstCapProfile` bootstrap window data and PROVISIONAL fixture.
 *
 * NOT RATIFIED PRODUCT BALANCE (phase brief §13/§119; post-beta backlog ⑧).
 * The numbers below are a live beta placeholder band, not calibration data
 * that has yet to be produced and not test-only fixtures either — they are
 * the numbers the running game currently uses, pending real per-rarity
 * balancing. Rarity ids reuse the exact `_PROVISIONAL` naming convention
 * G.1's own fixture already established (`test/skill-book-spec.test.ts`).
 *
 * Stage 4 (plan `sparkling-purring-allen`) collapsed the prior (rarity ×
 * reference band) 2-D table — every band row per rarity was already
 * byte-identical, since the band axis carried no real information once the
 * crafter-relative `phi(t, r)` multiplier (`burst/assess.ts`) replaced it —
 * to one row per rarity.
 *
 * Stage 2 (plan `sparkling-purring-allen`) re-expressed the caps in H-unit
 * (Reference-Basic-Attack-equivalent) terms and widened the fixture from 3
 * rarities to 5 (`rarity1_PROVISIONAL`..`rarity5_PROVISIONAL`), replacing the old
 * `low`/`mid`/`high_TEST_ONLY` naming — every caller/test that referenced
 * those three ids by name was updated alongside this file.
 *
 * The ids are spelled `rarity<n>_` rather than the plan's own `R1`..`R5`
 * shorthand: `R1` is already phase R.1 throughout this repo
 * (`R1_BOOTSTRAP_ENCOUNTERS`/`_PLAYERS`/`_PROFILE_SET`/`_PROVENANCE`, and the
 * `R1-R1`..`R1-R25` requirement ids in
 * `test/reference-profile-matrix.test.ts`), which has nothing to do with skill
 * book rarity. `tier<n>_` was not an option either — the absolute Tier I..V
 * table (`BalanceProfile.tiers`, since removed) was a separate axis, and
 * reusing its spelling would have conflated the two. `activationBurst`
 * runs 200H..250H in even 12.5H steps: 200H is `V13_MAX_REFERENCE_NORMAL_
 * Q_REF` (the Normal-profile M10 nominal maximum), 250H is R5's own base cap
 * (`calibration/skill-damage-v13.ts`'s `V13_TOP_B_PEAK_Q_REF` derivation pins
 * 250H x phi(1, r_advanced)/phi(1, V13_R_REF) = 271.4H as R5's absolute
 * crafter-anchor-surface peak). `shortWindowBurst`/`sustainedWindowOutput`
 * are a uniform x1.5/x3 of that same row's `activationBurst` at every tier —
 * one fixed burst *shape*, only the absolute scale moves with rarity.
 */

import type { BurstCapProfile, BurstWindowProfile, CastReachLimits } from './types.js';

/** Bootstrap standardized windows (phase brief §4.1): ~2s / ~10s. Versioned
 * profile data, not immutable product constants. */
export const B1_BOOTSTRAP_WINDOWS: BurstWindowProfile = {
  shortWindowS: 2,
  sustainedWindowS: 10,
};

function rowCast(maxAttackUnits: number, maxGenerations: number) {
  return { maxAttackUnits, maxGenerations };
}

function rowCaps(activationBurst: number) {
  return { activationBurst, shortWindowBurst: activationBurst * 1.5, sustainedWindowOutput: activationBurst * 3 };
}

export const PROVISIONAL_BURST_CAP_PROFILE: BurstCapProfile = {
  id: 'burst.PROVISIONAL-b1-fixture-v3',
  version: 'PROVISIONAL-b1-fixture-v3',
  windows: B1_BOOTSTRAP_WINDOWS,
  // Provisional beta placeholder band — NOT ratified product balance. Real
  // per-rarity balancing is post-beta backlog ⑧. Power budget derives from
  // these rows (skill-book-bootstrap.ts), so this is the single source for
  // the per-rarity scaling.
  rules: [
    // `cast` (PROVISIONAL, owner 10/16): attack units 16..32 and generations 2..3 by rarity.
    // The rarity5 row equals today's global limits (`limits.maxSpawnedAttacks` 32,
    // `limits.maxProcGeneration` 3), which is why it is also the row of a skill with no rarity.
    { rarity: 'rarity1_PROVISIONAL', caps: rowCaps(200), cast: rowCast(16, 2) },
    { rarity: 'rarity2_PROVISIONAL', caps: rowCaps(212.5), cast: rowCast(20, 2) },
    { rarity: 'rarity3_PROVISIONAL', caps: rowCaps(225), cast: rowCast(24, 3) },
    { rarity: 'rarity4_PROVISIONAL', caps: rowCaps(237.5), cast: rowCast(28, 3) },
    { rarity: 'rarity5_PROVISIONAL', caps: rowCaps(250), cast: rowCast(32, 3) },
  ],
  provenance: { kind: 'bootstrap' },
};

/**
 * The rarity whose `cast` row a skill with no rarity uses. Skills carry no rarity
 * today (only the book they were made under has one), so every saved and live
 * skill — the whole workshop — runs under this row, which is today's 32 / 3.
 */
export const DEFAULT_CAST_REACH_RARITY = 'rarity5_PROVISIONAL';

/**
 * The one reader of the cast limits. Validation (`assessCompositeCapability`),
 * the static gates and the runtime executor all read the table through here;
 * an unknown rarity is a defect, not a reason to fall back.
 */
export function castReachLimits(rarity: string = DEFAULT_CAST_REACH_RARITY, profile: BurstCapProfile = PROVISIONAL_BURST_CAP_PROFILE): CastReachLimits {
  const rule = profile.rules.find((candidate) => candidate.rarity === rarity);
  if (rule === undefined) throw new Error(`no cast limits for rarity "${rarity}"`);
  return rule.cast;
}

/**
 * The attack units one cast may release: its rarity's reach, narrowed (never
 * widened) by a spawned-attack limit — a balance profile's or a host's. With
 * no such limit, the reach itself.
 */
export function castUnitCap(spawnedAttackLimit: number | undefined, rarity?: string): number {
  const reach = castReachLimits(rarity).maxAttackUnits;
  return Math.min(spawnedAttackLimit ?? reach, reach);
}
